import { useMemo, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { addDays, dateKey } from '../shared/rng';

/** One milestone touched on a given day (started or finished). */
export interface DayMilestone {
  id: number;
  name: string;
}

/** One activity done on a given day. */
export interface DayActivity {
  id: number;
  title: string;
  category: string;
}

/** Everything that happened on a single calendar day. */
export interface DayEvents {
  activities: DayActivity[];
  started: DayMilestone[];
  finished: DayMilestone[];
}

/** Total number of logged actions on a day. */
export function dayActionCount(e: DayEvents | undefined): number {
  if (!e) return 0;
  return e.activities.length + e.started.length + e.finished.length;
}

/**
 * What the user has tapped in the calendar: either a single day or a whole week
 * (the latter only from a collapsed past-month row). `days` holds the in-range
 * ISO keys to aggregate for the breakdown panel.
 */
export type CalendarSelection =
  | { kind: 'day'; key: string; date: Date; days: string[] }
  | { kind: 'week'; key: string; start: Date; end: Date; days: string[] };

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Monday of the ISO week containing `d` (local time). */
function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - dow);
  return x;
}

function stripTime(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Weeks after birth we ever render (24 months ≈ 104 weeks). */
const MAX_WEEKS = 105;

interface DayCell {
  date: Date;
  key: string;
  inRange: boolean;
}

interface WeekRow {
  /** Stable id — the Monday's ISO key. */
  id: string;
  monthKey: string;
  monday: Date;
  days: DayCell[];
  /** In-range day keys (for aggregating week totals + breakdown). */
  inRangeKeys: string[];
}

interface MonthGroup {
  monthKey: string;
  month: number;
  year: number;
  weeks: WeekRow[];
}

interface Props {
  dob: Date;
  today?: Date;
  events: Map<string, DayEvents>;
  selection: CalendarSelection | null;
  onSelect: (sel: CalendarSelection) => void;
}

function tone(n: number): string {
  if (n <= 0) return 'var(--color-grey-soft)';
  if (n === 1) return 'color-mix(in oklch, var(--color-accent) 30%, var(--color-bg))';
  if (n === 2) return 'color-mix(in oklch, var(--color-accent) 55%, var(--color-bg))';
  if (n === 3) return 'color-mix(in oklch, var(--color-accent) 78%, var(--color-bg))';
  return 'var(--color-accent)';
}

/** Week boxes hold a whole week of actions, so they ramp on a coarser scale. */
function weekTone(n: number): string {
  if (n <= 0) return 'var(--color-grey-soft)';
  if (n <= 2) return 'color-mix(in oklch, var(--color-accent) 30%, var(--color-bg))';
  if (n <= 4) return 'color-mix(in oklch, var(--color-accent) 55%, var(--color-bg))';
  if (n <= 6) return 'color-mix(in oklch, var(--color-accent) 78%, var(--color-bg))';
  return 'var(--color-accent)';
}

/**
 * Calendar heat-grid of logged actions since birth, grouped by calendar month.
 *
 * The current month (and any month the user expands) shows the full per-day
 * grid — weeks as rows, weekdays Mon→Sun as 1/7-width columns. Past months
 * collapse to a single line whose boxes are that month's weeks; tapping the
 * month expands it to days, tapping a week box selects that week. A finished
 * milestone carries a trophy mark on its day (and on its week box).
 */
export function DashboardCalendar({ dob, today, events, selection, onSelect }: Props) {
  const _today = stripTime(today ?? new Date());

  const { months, currentMonthKey } = useMemo(() => {
    const startMon = mondayOf(dob);
    const dobDay = stripTime(dob);
    const lastWeek = Math.min(
      MAX_WEEKS - 1,
      Math.floor((mondayOf(_today).getTime() - startMon.getTime()) / (7 * 86_400_000)),
    );

    const groups = new Map<string, MonthGroup>();
    const order: string[] = [];
    let currentMonthKey = '';

    for (let w = 0; w <= lastWeek; w++) {
      const monday = addDays(startMon, w * 7);
      const monthKey = `${monday.getFullYear()}-${monday.getMonth()}`;
      const days: DayCell[] = [];
      const inRangeKeys: string[] = [];
      for (let d = 0; d < 7; d++) {
        const date = addDays(monday, d);
        const inRange = date >= dobDay && date <= _today;
        const key = dateKey(date);
        days.push({ date, key, inRange });
        if (inRange) inRangeKeys.push(key);
      }
      const week: WeekRow = { id: dateKey(monday), monthKey, monday, days, inRangeKeys };

      let g = groups.get(monthKey);
      if (!g) {
        g = { monthKey, month: monday.getMonth(), year: monday.getFullYear(), weeks: [] };
        groups.set(monthKey, g);
        order.push(monthKey);
      }
      g.weeks.push(week);

      // The month that holds today's week is "current" and stays expanded.
      if (days.some((c) => c.key === dateKey(_today))) currentMonthKey = monthKey;
    }

    return { months: order.map((k) => groups.get(k)!), currentMonthKey };
  }, [dob.getTime(), _today.getTime()]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const totals = useMemo(() => {
    const t = { actions: 0, finished: 0 };
    for (const e of events.values()) {
      t.actions += e.activities.length + e.started.length + e.finished.length;
      t.finished += e.finished.length;
    }
    return t;
  }, [events]);

  const weekActions = (wk: WeekRow) =>
    wk.inRangeKeys.reduce((s, k) => s + dayActionCount(events.get(k)), 0);
  const weekFinished = (wk: WeekRow) =>
    wk.inRangeKeys.some((k) => (events.get(k)?.finished.length ?? 0) > 0);

  const toggleMonth = (monthKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  };

  const selectDay = (c: DayCell) => {
    if (!c.inRange) return;
    onSelect({ kind: 'day', key: c.key, date: c.date, days: [c.key] });
  };
  const selectWeek = (wk: WeekRow) => {
    if (wk.inRangeKeys.length === 0) return;
    onSelect({
      kind: 'week',
      key: wk.id,
      start: wk.days[0].date,
      end: wk.days[6].date,
      days: wk.inRangeKeys,
    });
  };

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 pb-3.5">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-serif text-lg tracking-tight text-ink">Activity</h3>
          <p className="mt-px text-[11.5px] text-ink-2">
            {totals.actions} {totals.actions === 1 ? 'action' : 'actions'} logged · weeks since birth
          </p>
        </div>
        <span
          className="rounded-2xl px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-accent-ink"
          style={{ background: 'color-mix(in oklch, var(--color-accent) 12%, var(--color-bg))' }}
        >
          {totals.finished} won
        </span>
      </div>

      <div className="mt-3.5 flex flex-col gap-2.5">
        {months.map((m) => {
          const isCurrent = m.monthKey === currentMonthKey;
          const isExpanded = isCurrent || expanded.has(m.monthKey);
          const label = `${MONTHS[m.month]}${m.year !== _today.getFullYear() ? ` ’${String(m.year).slice(2)}` : ''}`;

          return (
            <div key={m.monthKey}>
              {/* Month header — tap to expand/collapse (current month is locked open) */}
              <button
                onClick={() => !isCurrent && toggleMonth(m.monthKey)}
                disabled={isCurrent}
                className="mb-1 flex w-full items-center gap-1.5"
              >
                <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-3">
                  {label}
                </span>
                {!isCurrent && (
                  <span className={isExpanded ? 'rotate-180 transition-transform' : 'transition-transform'}>
                    <Icon name="down" size={13} color="var(--color-ink-3)" />
                  </span>
                )}
                {isCurrent && <span className="text-[9px] uppercase tracking-wide text-ink-3">· now</span>}
              </button>

              {isExpanded ? (
                <ExpandedMonth
                  weeks={m.weeks}
                  events={events}
                  selection={selection}
                  onSelectDay={selectDay}
                />
              ) : (
                <CollapsedMonth
                  weeks={m.weeks}
                  selection={selection}
                  weekActions={weekActions}
                  weekFinished={weekFinished}
                  onSelectWeek={selectWeek}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center justify-between">
        <span className="flex items-center gap-1 text-[10.5px] text-ink-3">
          <Icon name="trophy" size={12} color="var(--color-ink-3)" /> milestone won
        </span>
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[10px] text-ink-3">Quiet</span>
          {[0, 1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className="block size-2.5 rounded-sm"
              style={{ background: tone(n), border: '0.5px solid rgba(33, 28, 21, 0.06)' }}
            />
          ))}
          <span className="ml-1 text-[10px] text-ink-3">Busy</span>
        </div>
      </div>
    </div>
  );
}

/* ── Expanded month: per-day grid ───────────────────────────────────── */

function ExpandedMonth({
  weeks,
  events,
  selection,
  onSelectDay,
}: {
  weeks: WeekRow[];
  events: Map<string, DayEvents>;
  selection: CalendarSelection | null;
  onSelectDay: (c: DayCell) => void;
}) {
  return (
    <>
      <div className="grid items-center" style={{ gridTemplateColumns: 'repeat(7, 1fr)', columnGap: 3 }}>
        {WEEKDAYS.map((d) => (
          <span key={d} className="text-center text-[9.5px] font-semibold tracking-wide text-ink-3">
            {d}
          </span>
        ))}
      </div>
      <div className="mt-1 flex flex-col" style={{ rowGap: 3 }}>
        {weeks.map((wk) => (
          <div key={wk.id} className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', columnGap: 3 }}>
            {wk.days.map((c) => {
              const e = events.get(c.key);
              const n = dayActionCount(e);
              const won = (e?.finished.length ?? 0) > 0;
              const isSel = selection?.kind === 'day' && selection.key === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => onSelectDay(c)}
                  disabled={!c.inRange}
                  aria-label={c.inRange ? `${c.date.toDateString()}: ${n} actions` : undefined}
                  className="relative flex aspect-square items-center justify-center rounded-[5px]"
                  style={{
                    background: c.inRange ? tone(n) : 'transparent',
                    border: isSel
                      ? '1.4px solid var(--color-ink)'
                      : `0.5px solid ${c.inRange ? 'rgba(33, 28, 21, 0.06)' : 'transparent'}`,
                    cursor: c.inRange ? 'pointer' : 'default',
                    opacity: c.inRange ? 1 : 0,
                  }}
                >
                  {won && (
                    <Icon name="trophy" size={11} color={n >= 3 ? 'var(--color-bg)' : 'var(--color-accent-ink)'} />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Collapsed month: one row of week boxes ─────────────────────────── */

function CollapsedMonth({
  weeks,
  selection,
  weekActions,
  weekFinished,
  onSelectWeek,
}: {
  weeks: WeekRow[];
  selection: CalendarSelection | null;
  weekActions: (wk: WeekRow) => number;
  weekFinished: (wk: WeekRow) => boolean;
  onSelectWeek: (wk: WeekRow) => void;
}) {
  return (
    <div className="flex items-stretch" style={{ gap: 3 }}>
      {weeks.map((wk) => {
        const n = weekActions(wk);
        const won = weekFinished(wk);
        const empty = wk.inRangeKeys.length === 0;
        const isSel = selection?.kind === 'week' && selection.key === wk.id;
        return (
          <button
            key={wk.id}
            onClick={() => onSelectWeek(wk)}
            disabled={empty}
            aria-label={`Week of ${wk.days[0].date.toDateString()}: ${n} actions`}
            className="relative flex h-7 flex-1 items-center justify-center gap-1 rounded-[6px]"
            style={{
              background: empty ? 'transparent' : weekTone(n),
              border: isSel
                ? '1.4px solid var(--color-ink)'
                : `0.5px solid ${empty ? 'transparent' : 'rgba(33, 28, 21, 0.06)'}`,
              cursor: empty ? 'default' : 'pointer',
              opacity: empty ? 0.4 : 1,
            }}
          >
            {won && <Icon name="trophy" size={12} color={n > 6 ? 'var(--color-bg)' : 'var(--color-accent-ink)'} />}
            {!empty && n > 0 && (
              <span
                className="text-[10px] font-semibold tabular-nums"
                style={{ color: n > 6 ? 'var(--color-bg)' : 'var(--color-accent-ink)' }}
              >
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
